/**
 * My work (PRD 10.2): every card that is this person's, across every
 * onboarding. What they can act on now is first, soonest due at the top;
 * what is coming is below it and quieter; what was done this week is a short
 * list under that, and anything older is folded away.
 *
 * On a screen wide enough for both, the card opens beside the list -- the
 * first one that needs them, until they choose another -- so working through
 * the queue never leaves it. Narrower, each card is a link to its own page.
 * The rows stay real links either way, so a middle-click still opens a tab.
 *
 * "Theirs" is `queueFor()`'s answer -- assigned to them, held by nobody in
 * their department, or waiting on their department's sign-off -- so this
 * screen and the engine cannot disagree about whose a card is.
 */

import { useEffect, useState } from "react";
import type { QueueCard } from "@utuwrk/core";
import { useOrg } from "../lib/org.js";
import { STATE_CLASS, dueText, plural, recentText, todayText, waitingText } from "../lib/words.js";
import { CardPane } from "./CardPage.js";
import { Link, Loading, PageHeader, Problem, SlaPill, StatePill, useLoad } from "./bits.js";

/** Wide enough for the list and a card side by side, after the sidebar has its share. */
const SPLIT = "(min-width: 72rem)";

const WEEK_MS = 7 * 86_400_000;

function useSplit(): boolean {
  const [split, setSplit] = useState(() => window.matchMedia(SPLIT).matches);
  useEffect(() => {
    const query = window.matchMedia(SPLIT);
    const update = () => setSplit(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return split;
}

interface Picked {
  id: string;
  key: string;
}

const same = (entry: QueueCard, picked: Picked | null) =>
  picked !== null && entry.instanceId === picked.id && entry.card.key === picked.key;

/** One card as somebody's work: in their queue, and among an employee's open cards. */
export function QueueRow({
  entry,
  later,
  about = true,
  selected,
  onPick,
}: {
  entry: QueueCard;
  later?: boolean | undefined;
  about?: boolean;
  selected?: boolean;
  onPick?: (() => void) | undefined;
}) {
  const { nameOf, org } = useOrg();
  const { card } = entry;
  const signing = card.full && card.department !== org.department && card.canSignFor.length > 0;
  const late = entry.sla === "overdue" || entry.sla === "critical";
  return (
    <li>
      <Link
        to={{ name: "card", id: entry.instanceId, key: card.key }}
        className={`card card-link${later ? " card-later" : ""}`}
        current={selected ? "true" : undefined}
        instead={onPick}
      >
        <div className="card-top">
          <span className={`card-dot ${late ? "s-danger" : STATE_CLASS[card.state]}`} aria-hidden="true" />
          <h3 className="card-title">{card.title}</h3>
          <StatePill state={card.state} />
        </div>
        <p className="card-meta">
          {about ? <span className="card-who">{entry.employeeName}</span> : null}
          <span className="chip">{card.department}</span>
          {card.full ? <span>{dueText(card)}</span> : null}
          <SlaPill sla={entry.sla} />
          {signing ? <span className="chip chip-accent">Sign-off</span> : null}
          {card.full && card.assigneeId === null ? (
            <span>Unassigned: anyone in {card.department}</span>
          ) : null}
          {card.full && card.assigneeId !== null && signing ? (
            <span>Held by {nameOf(card.assigneeId)}</span>
          ) : null}
        </p>
      </Link>
    </li>
  );
}

/**
 * For an admin whose organisation has no site yet -- a new one, just made in
 * the console -- the way to the setup wizard, above everything else.
 */
function SetupPrompt() {
  const { api, org } = useOrg();
  const admin = org.role === "admin";
  const { data } = useLoad(() => (admin ? api.setup() : Promise.resolve(null)), [api, admin]);
  if (!admin || !data || data.levels.some((level) => level.kind === "site")) return null;
  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Set up {org.name}</h2>
      <p>Sites, departments, people and who does what, then your first onboarding. About fifteen minutes.</p>
      <div className="action-row">
        <Link to={{ name: "setup" }} className="button">
          Start the setup
        </Link>
      </div>
    </div>
  );
}

export function Queue() {
  const { api } = useOrg();
  const { data, error, reload } = useLoad(() => api.queue(), [api]);
  const split = useSplit();
  const [picked, setPicked] = useState<Picked | null>(null);

  if (!data && !error) return <Loading title="My work" />;

  const now = Date.now();
  const finishedAt = (entry: QueueCard) => (entry.card.full ? (entry.card.finishedAt ?? 0) : 0);
  const done = [...(data?.done ?? [])].sort((a, b) => finishedAt(b) - finishedAt(a));
  const recent = done.filter((entry) => now - finishedAt(entry) < WEEK_MS);
  const earlier = done.filter((entry) => now - finishedAt(entry) >= WEEK_MS);

  // Beside the list: what they chose, else the first thing that needs them.
  const first = data?.now[0] ?? data?.upcoming[0];
  const shown: Picked | null = split
    ? (picked ?? (first ? { id: first.instanceId, key: first.card.key } : null))
    : null;

  const row = (entry: QueueCard, later?: boolean) => (
    <QueueRow
      key={`${entry.instanceId}:${entry.card.key}`}
      entry={entry}
      later={later}
      selected={same(entry, shown)}
      onPick={split ? () => setPicked({ id: entry.instanceId, key: entry.card.key }) : undefined}
    />
  );

  return (
    <section className="page">
      <PageHeader
        eyebrow={`My work · ${todayText(new Date(now))}`}
        title={data ? waitingText(data.now.length) : "My work"}
      />
      <Problem>{error}</Problem>
      <SetupPrompt />
      <div className={split ? "work" : "page"}>
        <div className="panel work-list">
          {data ? (
            <>
              <section className="zone" aria-labelledby="zone-now">
                <h2 id="zone-now" className="zone-title">
                  Now <span className="zone-count">{data.now.length}</span>
                </h2>
                {data.now.length === 0 ? (
                  <p className="empty-line">
                    Nothing needs you right now. When a card becomes yours you get a notice.
                  </p>
                ) : (
                  <ul className="cards">{data.now.map((entry) => row(entry))}</ul>
                )}
              </section>

              <section className="zone" aria-labelledby="zone-upcoming">
                <h2 id="zone-upcoming" className="zone-title">
                  Coming up <span className="zone-count">{data.upcoming.length}</span>
                </h2>
                {data.upcoming.length === 0 ? (
                  <p className="empty-line">Nothing is on its way to you.</p>
                ) : (
                  <ul className="cards">{data.upcoming.map((entry) => row(entry, true))}</ul>
                )}
              </section>

              {recent.length > 0 ? (
                <section className="zone" aria-labelledby="zone-done">
                  <h2 id="zone-done" className="zone-title">
                    Done this week <span className="zone-count">{recent.length}</span>
                  </h2>
                  <ul className="done-list">
                    {recent.map((entry) => (
                      <li key={`${entry.instanceId}:${entry.card.key}`}>
                        <svg viewBox="0 0 12 12" aria-hidden="true" className="done-tick">
                          <path d="M2.8 6.2l2.2 2.2 4.2-4.6" />
                        </svg>
                        <Link
                          to={{ name: "card", id: entry.instanceId, key: entry.card.key }}
                          current={same(entry, shown) ? "true" : undefined}
                          instead={
                            split ? () => setPicked({ id: entry.instanceId, key: entry.card.key }) : undefined
                          }
                        >
                          {entry.card.title}
                        </Link>
                        <span className="muted">
                          {entry.employeeName} · {recentText(finishedAt(entry), now)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {earlier.length > 0 ? (
                <details className="zone-done">
                  <summary>Done before this week: {plural(earlier.length, "card")}</summary>
                  <ul className="cards">{earlier.map((entry) => row(entry, true))}</ul>
                </details>
              ) : null}
            </>
          ) : null}
        </div>

        {split ? (
          <div className="work-card">
            {shown ? (
              <CardPane id={shown.id} cardKey={shown.key} onChange={reload} />
            ) : (
              <div className="panel">
                <p className="muted">Nothing is yours yet. When a card becomes yours you get a notice.</p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}
